/**
 * CompensationErrorBoundary Component
 *
 * A specialized error boundary for the Compensation Management section.
 * Provides contextual error messages and recovery options specific to compensation features.
 *
 * Usage:
 * ```tsx
 * <CompensationErrorBoundary>
 *   <CompensationPage />
 * </CompensationErrorBoundary>
 * ```
 */

import React, { Component, ErrorInfo, ReactNode } from 'react';
import * as Sentry from '@sentry/react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';
import { Button } from './ui/Button';
import { Alert } from './ui/Alert';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
}

/**
 * Class-based error boundary specifically for compensation features.
 * Catches React errors and displays compensation-specific recovery options.
 */
export class CompensationErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log error to console for debugging
    console.error('[CompensationErrorBoundary] Uncaught error:', error, errorInfo);

    // Send to Sentry with compensation-specific tags
    Sentry.captureException(error, {
      contexts: {
        react: {
          componentStack: errorInfo.componentStack,
        },
      },
      tags: {
        errorType: 'compensation_error_boundary',
        section: 'compensation_management',
      },
      level: 'error',
    });

    // Update state with error details
    this.setState({ error, errorInfo });
  }

  handleReload = () => {
    // Clear error state and reload page
    this.setState({ hasError: false, error: undefined, errorInfo: undefined });
    window.location.reload();
  };

  handleGoToCompensationHome = () => {
    // Clear error and navigate to compensation home
    this.setState({ hasError: false, error: undefined, errorInfo: undefined });
    window.location.href = '/dashboard/admin/compensation';
  };

  handleGoToDashboard = () => {
    // Clear error and navigate to dashboard
    this.setState({ hasError: false, error: undefined, errorInfo: undefined });
    window.location.href = '/dashboard';
  };

  render() {
    if (this.state.hasError) {
      // Compensation-specific fallback UI
      return (
        <div className="min-h-screen bg-surface-secondary flex items-center justify-center p-6">
          <div className="max-w-2xl w-full">
            <Alert variant="error" className="mb-6">
              <AlertTriangle className="h-6 w-6" />
              <div>
                <h1 className="text-xl font-bold mb-2">Compensation Management Error</h1>
                <p className="text-sm">
                  We encountered an error while loading the compensation management system. This could be due to:
                </p>
                <ul className="list-disc list-inside mt-2 text-sm space-y-1">
                  <li>Network connectivity issues</li>
                  <li>Temporary service disruption</li>
                  <li>Invalid data format</li>
                  <li>Missing required permissions</li>
                </ul>
              </div>
            </Alert>

            <div className="bg-surface-primary rounded-2xl border border-border-primary p-6">
              <h2 className="text-lg font-semibold text-text-primary mb-4">
                What would you like to do?
              </h2>

              <div className="space-y-3">
                <Button
                  onClick={this.handleReload}
                  variant="primary"
                  className="w-full flex items-center justify-center gap-2"
                >
                  <RefreshCw className="h-4 w-4" />
                  Try Again
                </Button>

                <Button
                  onClick={this.handleGoToCompensationHome}
                  variant="secondary"
                  className="w-full flex items-center justify-center gap-2"
                >
                  <Home className="h-4 w-4" />
                  Go to Compensation Home
                </Button>

                <Button
                  onClick={this.handleGoToDashboard}
                  variant="secondary"
                  className="w-full flex items-center justify-center gap-2"
                >
                  <Home className="h-4 w-4" />
                  Go to Dashboard
                </Button>
              </div>

              {/* Error Details (Development only) */}
              {import.meta.env.DEV && this.state.error && (
                <div className="mt-6 p-4 bg-surface-secondary rounded border border-border-secondary">
                  <h3 className="text-sm font-semibold text-text-primary mb-2">
                    Error Details (dev only)
                  </h3>
                  <pre className="text-xs overflow-auto text-text-secondary whitespace-pre-wrap break-words max-h-60">
                    {this.state.error.toString()}
                    {'\n\n'}
                    {this.state.error.stack}
                  </pre>

                  {this.state.errorInfo?.componentStack && (
                    <div className="mt-4">
                      <h4 className="text-xs font-semibold text-text-primary mb-2">
                        Component Stack:
                      </h4>
                      <pre className="text-xs overflow-auto text-text-secondary whitespace-pre-wrap break-words max-h-40">
                        {this.state.errorInfo.componentStack}
                      </pre>
                    </div>
                  )}
                </div>
              )}

              <div className="mt-6 text-center text-sm text-text-secondary">
                <p>If this problem persists, please contact support:</p>
                <a
                  href="mailto:support@etchare.com"
                  className="text-primary-500 hover:underline"
                >
                  support@etchare.com
                </a>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

/**
 * HOC to wrap a compensation component with the specialized error boundary.
 *
 * @example
 * ```tsx
 * const SafeCompensationPage = withCompensationErrorBoundary(CompensationAnalyticsPage);
 * ```
 */
export function withCompensationErrorBoundary<P extends object>(
  Component: React.ComponentType<P>
) {
  return function WrappedComponent(props: P) {
    return (
      <CompensationErrorBoundary>
        <Component {...props} />
      </CompensationErrorBoundary>
    );
  };
}
